import { AxiosError } from 'axios';
import { DefaultFilters, SavedFilters, StateMachineState, Test } from './types';
import { patientPropsSelector } from '../../../src/redux/selectors/patientSelector';
import { Subject } from '@lifeomic/phc-web-toolkit/src/common/types/Subject';
import { useMaskedMode } from '@lifeomic/phc-web-toolkit/src/hooks/useMaskedMode';
import * as React from 'react';
import GenomicsService from '@lifeomic/phc-web-toolkit/src/services/GenomicsService';
import useService from '@lifeomic/phc-web-toolkit/src/hooks/useService';
import { useActiveMrnConfig } from '@lifeomic/phc-web-toolkit/src/hooks/useActiveMrnConfig';

const genomicsService = new GenomicsService();

const transformSoftDeletesToHydrateFormat = (
  softDeletesData: any[],
  subjectId: string,
) => {
  if (!softDeletesData?.length) {
    return null;
  }

  const tests = softDeletesData.map((item, index) => {
    const test = {
      id: item.testId || `soft-delete-${index}`,
      name: item.metadata?.name || 'Soft Deleted Test',
      testType: `${item.metadata?.testType || 'Unknown'} (archived)`,
      patientId: subjectId,
      createdDate: new Date().toISOString(),
      indexedDate: new Date().toISOString(),
      sourceFileId: null,
      reportFileId: null,
      status: 'ACTIVE' as const,
      referenceSetId: item.metadata?.referenceSetId || 'GRCh38',
      sets: [],
      tasks: [],
      bodySite: item.metadata?.bodySite,
      nhc: item.metadata?.nhc || [],
      ihc: item.metadata?.ihc || [],
      hla: item.metadata?.hla || [],
      hyperdiploidyTrisomies: [],
      caseId: item.caseId || item.metadata?.caseId,
      metadata: item.metadata,
    };
    return test;
  });

  return {
    tests,
  };
};

export interface UseHydrateOmicsExplorer {
  defaultFilters?: DefaultFilters;
  savedFilters?: SavedFilters;
  state: StateMachineState;
  subject?: Subject;
  tests?: Array<Test>;
  isMaskedModeEnabled?: boolean;
  error?: AxiosError;
  refetchTests?: () => void;
}

/**
 * @description A hook for hydrating the Omics Explorer. It makes a call
 * to an endpoint to load all of the information to hydrate the UI given
 * a project and subject ID.
 */
export function useHydrateOmicsExplorer({
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  activeProjectId,
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  subjectId,
}): UseHydrateOmicsExplorer {
  const [hyrdateOmicsExplorer, hyrdateOmicsExplorerState] = useService(
    genomicsService.hydrateOmicsExplorer,
  );

  const [getSoftDeletes, getSoftDeletesState] = useService(
    genomicsService.getSoftDeletes,
  );

  const [responseDefaultFilters, setResponseDefaultFilters] =
    React.useState<any>(null);
  const [responseSavedFilters, setResponseSavedFilters] =
    React.useState<any>(null);
  const [responseSubject, setResponseSubject] = React.useState<any>(null);
  const [responseTests, setResponseTests] = React.useState<any[]>([]);
  const [softDeletesData, setSoftDeletesData] = React.useState<any>(null);

  const [maybeMaskedAndProppedSubject, setMaybeMaskedAndProppedSubject] =
    React.useState<any>(null);

  const isMaskedModeEnabled = useMaskedMode();
  const mrnConfig = useActiveMrnConfig();

  // Check for soft-deletes first, then hydrate if no soft-deletes found
  React.useEffect(() => {
    if (!activeProjectId) {
      return;
    }

    if (!subjectId) {
      return;
    }

    getSoftDeletes(activeProjectId, subjectId);
  }, [activeProjectId, getSoftDeletes, subjectId]);

  React.useEffect(() => {
    if (!getSoftDeletesState.response?.data) {
      return;
    }

    const responseData = getSoftDeletesState.response.data;
    const softDeletesData = responseData?.softDeletes || [];

    if (softDeletesData.length > 0) {
      const transformedData = transformSoftDeletesToHydrateFormat(
        softDeletesData,
        subjectId,
      );

      if (transformedData) {
        setSoftDeletesData(transformedData);
      }
    }
  }, [getSoftDeletesState.response, subjectId]);

  React.useEffect(() => {
    if (!activeProjectId || !subjectId) {
      return;
    }

    hyrdateOmicsExplorer({ projectId: activeProjectId, subjectId });
  }, [activeProjectId, hyrdateOmicsExplorer, subjectId]);

  // Get the response from the API and set to local variables
  React.useEffect(() => {
    if (!hyrdateOmicsExplorerState?.response?.data) {
      return;
    }

    setResponseDefaultFilters(
      hyrdateOmicsExplorerState.response.data.defaultFilters,
    );
    setResponseSavedFilters(
      hyrdateOmicsExplorerState.response.data.savedFilters,
    );
    setResponseSubject(hyrdateOmicsExplorerState.response.data.subject);

    // Combine soft-deleted tests with active tests
    const hydrateTests = hyrdateOmicsExplorerState.response.data.tests || [];
    if (softDeletesData?.tests) {
      // Show active tests first, then soft-deleted tests last
      setResponseTests([...hydrateTests, ...softDeletesData.tests]);
    } else {
      setResponseTests(hydrateTests);
    }
  }, [hyrdateOmicsExplorerState, softDeletesData]);

  // We want to take the subject from the API response and run it
  // through our masked-mode checks and formatter
  React.useEffect(() => {
    if (!responseSubject) {
      return;
    }

    const maybeMaskedAndFormattedSubject = patientPropsSelector(
      responseSubject,
      isMaskedModeEnabled,
      mrnConfig,
    );

    setMaybeMaskedAndProppedSubject(maybeMaskedAndFormattedSubject);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isMaskedModeEnabled,
    responseSubject,
    mrnConfig.configType,
    mrnConfig?.code,
    mrnConfig.system,
  ]);

  const refetchTests = React.useCallback(() => {
    if (!activeProjectId || !subjectId) {
      return;
    }
    getSoftDeletes(activeProjectId, subjectId);
    hyrdateOmicsExplorer({ projectId: activeProjectId, subjectId });
  }, [activeProjectId, subjectId, getSoftDeletes, hyrdateOmicsExplorer]);

  if (softDeletesData && responseSubject) {
    return {
      defaultFilters: responseDefaultFilters || undefined,
      savedFilters: responseSavedFilters || undefined,
      state: 'COMPLETE',
      subject: maybeMaskedAndProppedSubject,
      tests: responseTests,
      isMaskedModeEnabled,
      error: undefined,
      refetchTests,
    };
  }

  // If we haven't gotten a subject back from the API yet,
  // we just return an initializing state
  if (!maybeMaskedAndProppedSubject && !hyrdateOmicsExplorerState.error) {
    return {
      state: 'INITIALIZING',
      refetchTests,
    };
  }

  // Otherwise it's safe to assume we are cleared to render and do
  // OE-things now
  return {
    defaultFilters: responseDefaultFilters || undefined,
    isMaskedModeEnabled,
    savedFilters: responseSavedFilters || undefined,
    state: hyrdateOmicsExplorerState.error ? 'ERROR' : 'COMPLETE',
    subject: maybeMaskedAndProppedSubject,
    tests: responseTests,
    error: hyrdateOmicsExplorerState.error,
    refetchTests,
  };
}
